import { useState } from "react";
import * as XLSX from "xlsx";
import { useFetch } from "../hooks/useFetch";
import {
  confirmCashPaymentByAdmin,
  getPendingCashPaymentsByAdmin,
  rejectCashPaymentByAdmin,
  markDepositedToBankByAdmin,
} from "../api/subscriptionsApi";
import AccessControl from "../components/AccessControl";

// ─── Inline styles (no external CSS dependency) ──────────────────────────────
const S = {
  page: {
    minHeight: "100vh",
    background: "#f5f6fa",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    padding: "24px 20px 48px",
    boxSizing: "border-box",
  },
  wrap: { maxWidth: 1100, margin: "0 auto" },

  // Header
  header: { marginBottom: 28 },
  headerTop: { display: "flex", alignItems: "center", gap: 14, marginBottom: 4 },
  headerIcon: {
    width: 42, height: 42, borderRadius: 10,
    background: "linear-gradient(135deg,#6366f1,#4f46e5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 20,
  },
  h1: { margin: 0, fontSize: 22, fontWeight: 700, color: "#111827" },
  subtitle: { margin: 0, fontSize: 13.5, color: "#6b7280", paddingLeft: 56 },

  // Stats row
  statsRow: { display: "flex", gap: 14, marginBottom: 28, flexWrap: "wrap" },
  statCard: {
    flex: "1 1 160px", background: "#fff", borderRadius: 12,
    padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,.07)",
    borderTop: "3px solid",
  },
  statLabel: { fontSize: 11.5, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6 },
  statValue: { fontSize: 26, fontWeight: 800, color: "#111827", lineHeight: 1 },
  statSub:   { fontSize: 12, color: "#6b7280", marginTop: 4 },

  // Section
  section: { marginBottom: 32 },
  sectionHead: {
    display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
  },
  dot: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: "#374151" },
  pill: {
    fontSize: 11.5, fontWeight: 700, padding: "2px 9px",
    borderRadius: 20, lineHeight: 1.6,
  },

  // Table card
  card: {
    background: "#fff", borderRadius: 14,
    boxShadow: "0 1px 4px rgba(0,0,0,.08)",
    overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "11px 16px", fontSize: 11.5, fontWeight: 700,
    color: "#6b7280", textTransform: "uppercase", letterSpacing: ".05em",
    background: "#f9fafb", borderBottom: "1px solid #e5e7eb",
    textAlign: "left",
  },
  td: {
    padding: "13px 16px", fontSize: 13.5, color: "#374151",
    borderBottom: "1px solid #f3f4f6",
  },
  driverName:  { fontWeight: 600, color: "#111827", margin: 0, fontSize: 13.5 },
  driverPhone: { margin: 0, fontSize: 12, color: "#9ca3af", marginTop: 2 },

  badge: {
    display: "inline-block", padding: "3px 10px", borderRadius: 6,
    fontSize: 12, fontWeight: 600, background: "#ede9fe", color: "#6d28d9",
  },
  amount: { fontWeight: 700, color: "#111827", fontSize: 14 },
  muted:  { color: "#d1d5db", fontSize: 13 },
  receiptTag: {
    fontSize: 11.5, fontWeight: 600, color: "#0369a1",
    background: "#e0f2fe", padding: "2px 8px", borderRadius: 5,
  },

  // Action buttons
  btnGroup: { display: "flex", gap: 8, flexWrap: "wrap" },
  btnConfirm: {
    padding: "6px 14px", fontSize: 12.5, fontWeight: 600,
    background: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0",
    borderRadius: 7, cursor: "pointer",
  },
  btnReject: {
    padding: "6px 14px", fontSize: 12.5, fontWeight: 600,
    background: "#fee2e2", color: "#b91c1c", border: "1px solid #fecaca",
    borderRadius: 7, cursor: "pointer",
  },
  btnDeposit: {
    padding: "6px 14px", fontSize: 12.5, fontWeight: 600,
    background: "#dbeafe", color: "#1d4ed8", border: "1px solid #bfdbfe",
    borderRadius: 7, cursor: "pointer",
  },

  // Empty
  empty: { textAlign: "center", padding: "48px 24px", color: "#9ca3af" },
  emptyIcon: { fontSize: 40, marginBottom: 10 },
  emptyTitle: { fontWeight: 600, color: "#374151", margin: "0 0 4px" },
  emptySub:   { fontSize: 13, margin: 0 },

  // Skeleton
  skeleton: { padding: "20px 16px", display: "flex", flexDirection: "column", gap: 12 },
  skRow: { height: 52, background: "#f3f4f6", borderRadius: 8, animation: "pulse 1.4s ease infinite" },

  // Toast
  toast: {
    position: "fixed", bottom: 24, right: 24, zIndex: 9999,
    background: "#1e293b", color: "#fff", padding: "12px 20px",
    borderRadius: 10, fontSize: 13.5, fontWeight: 500,
    boxShadow: "0 4px 16px rgba(0,0,0,.25)",
    maxWidth: 340,
  },

  // Modal
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
    zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center",
    padding: 16,
  },
  modal: {
    background: "#fff", borderRadius: 16, width: "100%", maxWidth: 440,
    boxShadow: "0 20px 60px rgba(0,0,0,.25)", overflow: "hidden",
  },
  modalHeader: {
    padding: "18px 22px", display: "flex",
    justifyContent: "space-between", alignItems: "center",
  },
  modalTitle: { margin: 0, fontSize: 16, fontWeight: 700 },
  modalClose: {
    background: "none", border: "none", fontSize: 22, cursor: "pointer",
    color: "#9ca3af", lineHeight: 1, padding: 0,
  },
  modalBody: { padding: "4px 22px 22px" },

  // Detail card inside modal
  detailCard: {
    background: "#f9fafb", borderRadius: 10,
    border: "1px solid #e5e7eb", padding: "14px 16px",
    marginBottom: 16,
  },
  detailRow: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", padding: "5px 0",
  },
  detailLabel: { fontSize: 12.5, color: "#9ca3af", fontWeight: 500 },
  detailValue: { fontSize: 13.5, color: "#111827", fontWeight: 600 },
  detailAmount: { fontSize: 18, fontWeight: 800, color: "#111827" },

  // Banner inside modal
  banner: {
    display: "flex", gap: 10, alignItems: "flex-start",
    background: "#eff6ff", border: "1px solid #bfdbfe",
    borderRadius: 9, padding: "10px 14px", marginBottom: 14,
    fontSize: 13, color: "#1e40af",
  },
  bannerDanger: { background: "#fff7ed", border: "1px solid #fed7aa", color: "#92400e" },

  // Field
  fieldLabel: { display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 6 },
  fieldReq:   { color: "#ef4444" },
  fieldInput: {
    width: "100%", padding: "9px 12px", fontSize: 13.5,
    border: "1.5px solid #e5e7eb", borderRadius: 8, outline: "none",
    boxSizing: "border-box", color: "#111827", fontFamily: "inherit",
    resize: "vertical",
  },
  fieldInputDanger: { borderColor: "#fca5a5" },

  // Modal actions
  modalActions: { display: "flex", gap: 10, marginTop: 18 },
  btnCancel: {
    flex: 1, padding: "10px", fontSize: 13.5, fontWeight: 600,
    background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb",
    borderRadius: 8, cursor: "pointer",
  },
  btnActivate: {
    flex: 2, padding: "10px", fontSize: 13.5, fontWeight: 700,
    background: "linear-gradient(135deg,#22c55e,#16a34a)",
    color: "#fff", border: "none", borderRadius: 8, cursor: "pointer",
  },
  btnRejectMd: {
    flex: 2, padding: "10px", fontSize: 13.5, fontWeight: 700,
    background: "linear-gradient(135deg,#ef4444,#b91c1c)",
    color: "#fff", border: "none", borderRadius: 8, cursor: "pointer",
  },
  btnBank: {
    flex: 2, padding: "10px", fontSize: 13.5, fontWeight: 700,
    background: "linear-gradient(135deg,#3b82f6,#1d4ed8)",
    color: "#fff", border: "none", borderRadius: 8, cursor: "pointer",
  },
  btnExcel: {
    marginLeft: "auto",
    padding: "6px 14px", fontSize: 12.5, fontWeight: 600,
    background: "#16a34a", color: "#fff",
    border: "none", borderRadius: 7, cursor: "pointer",
    display: "flex", alignItems: "center", gap: 6,
    whiteSpace: "nowrap",
  },
};

// ─── Utility ──────────────────────────────────────────────────────────────────
function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function PendingCash() {
  const { data, loading, error, refetch } = useFetch(getPendingCashPaymentsByAdmin);

  const [selected,      setSelected]      = useState(null);
  const [modalType,     setModalType]     = useState(null); // 'confirm' | 'reject' | 'deposit'
  const [notes,         setNotes]         = useState("");
  const [rejectReason,  setRejectReason]  = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [busy,          setBusy]          = useState(false);
  const [toast,         setToast]         = useState(null); // { msg, ok }

  const allRows = Array.isArray(data)
    ? data
    : (data?.payments ?? data?.data ?? data?.records ?? []);

  const pendingRows   = allRows.filter((r) => r.status === "pending");
  const confirmedRows = allRows.filter((r) => r.status === "paid" && !r.depositedToBank);

  const totalPendingAmt   = pendingRows.reduce((s, r)   => s + (r.amount || 0), 0);
  const totalConfirmedAmt = confirmedRows.reduce((s, r) => s + (r.amount || 0), 0);

  const adminId = localStorage.getItem("adminId");

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const openModal = (row, type) => { setSelected(row); setModalType(type); };
  const closeModal = () => {
    setSelected(null); setModalType(null);
    setNotes(""); setRejectReason(""); setReceiptNumber("");
  };

  // ─── Confirm ───────────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await confirmCashPaymentByAdmin({ paymentId: selected._id, adminId, notes });
      if (res?.success === false) throw new Error(res.error || "Failed");
      showToast("Payment confirmed — subscription activated.");
      closeModal(); refetch();
    } catch (e) {
      showToast(e?.message || "Something went wrong", false);
    } finally { setBusy(false); }
  };

  // ─── Reject ────────────────────────────────────────────────────────────────
  const handleReject = async () => {
    if (!selected) return;
    if (!rejectReason.trim()) { showToast("Enter a reason for rejection.", false); return; }
    setBusy(true);
    try {
      const res = await rejectCashPaymentByAdmin({ paymentId: selected._id, adminId, reason: rejectReason });
      if (res?.success === false) throw new Error(res.error || "Failed");
      showToast("Payment marked as Not Paid.");
      closeModal(); refetch();
    } catch (e) {
      showToast(e?.message || "Something went wrong", false);
    } finally { setBusy(false); }
  };

  // ─── Mark Deposited ────────────────────────────────────────────────────────
  const handleMarkDeposited = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await markDepositedToBankByAdmin({
        paymentId: selected._id, adminId,
        receiptNumber: receiptNumber.trim() || undefined,
      });
      if (res?.success === false) throw new Error(res.error || "Failed");
      showToast("Marked as deposited to bank. Awaiting superadmin verification.");
      closeModal(); refetch();
    } catch (e) {
      showToast(e?.message || "Something went wrong", false);
    } finally { setBusy(false); }
  };

  // ─── Export Confirmed (Pending Bank Deposit) to Excel ─────────────────────
  const exportConfirmedToExcel = () => {
    if (!confirmedRows.length) {
      showToast("No records to export.", false);
      return;
    }

    const rows = confirmedRows.map((r, i) => ({
      "#":             i + 1,
      "Driver Name":   r.driverId?.name  || "—",
      "Phone":         r.driverId?.phone || "—",
      "Plan":          r.planId?.name    || "—",
      "Amount (₹)":    r.amount          || 0,
      "Confirmed At":  r.paidAt          ? new Date(r.paidAt).toLocaleString("en-IN") : "—",
      "Collected By":  r.collectedBy?.name || "—",
      "Payment ID":    r._id             || "—",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);

    // Column widths
    ws["!cols"] = [
      { wch: 4 }, { wch: 22 }, { wch: 14 }, { wch: 14 },
      { wch: 12 }, { wch: 22 }, { wch: 18 }, { wch: 26 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pending Deposits");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `pending-bank-deposits_${today}.xlsx`);
    showToast("Excel downloaded successfully.");
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <AccessControl
        allowedRoles={["SUPERADMIN", "ADMIN", "NSM", "RSM", "ASM", "ACCOUNTS"]}
        redirectTo="/404"
      />

      <div style={S.wrap}>
        {/* Header */}
        <div style={S.header}>
          <div style={S.headerTop}>
            <div style={S.headerIcon}>💵</div>
            <h1 style={S.h1}>Pending Cash Payments</h1>
          </div>
          <p style={S.subtitle}>
            Drivers who paid cash at office — confirm to activate their subscription
          </p>
        </div>

        {/* Stats */}
        {!loading && (
          <div style={S.statsRow}>
            <div style={{ ...S.statCard, borderTopColor: "#f59e0b" }}>
              <div style={S.statLabel}>Awaiting Confirmation</div>
              <div style={S.statValue}>{pendingRows.length}</div>
              <div style={S.statSub}>₹{totalPendingAmt.toLocaleString()} pending</div>
            </div>
            <div style={{ ...S.statCard, borderTopColor: "#3b82f6" }}>
              <div style={S.statLabel}>Confirmed · Not Deposited</div>
              <div style={S.statValue}>{confirmedRows.length}</div>
              <div style={S.statSub}>₹{totalConfirmedAmt.toLocaleString()} to deposit</div>
            </div>
            <div style={{ ...S.statCard, borderTopColor: "#8b5cf6" }}>
              <div style={S.statLabel}>Total Cash in Hand</div>
              <div style={{ ...S.statValue, color: "#6d28d9" }}>
                ₹{(totalPendingAmt + totalConfirmedAmt).toLocaleString()}
              </div>
              <div style={S.statSub}>{allRows.length} records total</div>
            </div>
          </div>
        )}

        {loading ? (
          <div style={S.skeleton}>
            {[1, 2, 3, 4, 5].map((i) => <div key={i} style={S.skRow} />)}
          </div>
        ) : error ? (
          <div style={{ ...S.empty }}>
            <div style={S.emptyIcon}>⚠️</div>
            <p style={S.emptyTitle}>Failed to load</p>
            <p style={S.emptySub}>{error}</p>
          </div>
        ) : (
          <>
            {/* ── Section 1: Awaiting Confirmation ── */}
            <div style={S.section}>
              <div style={S.sectionHead}>
                <div style={{ ...S.dot, background: "#f59e0b" }} />
                <span style={S.sectionTitle}>Awaiting Confirmation</span>
                <span style={{ ...S.pill, background: "#fef3c7", color: "#92400e" }}>
                  {pendingRows.length}
                </span>
              </div>

              {pendingRows.length === 0 ? (
                <div style={{ ...S.card, ...S.empty }}>
                  <div style={S.emptyIcon}>🎉</div>
                  <p style={S.emptyTitle}>All clear!</p>
                  <p style={S.emptySub}>No cash payments waiting for confirmation.</p>
                </div>
              ) : (
                <div style={S.card}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        {["Driver", "Plan", "Amount", "Requested At", "Collected By", "Actions"].map((h) => (
                          <th key={h} style={S.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pendingRows.map((r) => (
                        <tr key={r._id}>
                          <td style={S.td}>
                            <p style={S.driverName}>{r.driverId?.name || "—"}</p>
                            <p style={S.driverPhone}>{r.driverId?.phone || ""}</p>
                          </td>
                          <td style={S.td}>
                            <span style={S.badge}>{r.planId?.name || "—"}</span>
                          </td>
                          <td style={S.td}>
                            <span style={S.amount}>₹{r.amount?.toLocaleString()}</span>
                          </td>
                          <td style={{ ...S.td, fontSize: 12.5, color: "#6b7280" }}>
                            {fmt(r.createdAt)}
                          </td>
                          <td style={S.td}>
                            {r.collectedBy?.name
                              ? <span style={{ fontSize: 13, color: "#374151" }}>{r.collectedBy.name}</span>
                              : <span style={S.muted}>—</span>}
                          </td>
                          <td style={S.td}>
                            <div style={S.btnGroup}>
                              <button
                                style={S.btnConfirm}
                                onClick={() => openModal(r, "confirm")}
                              >
                                ✓ Confirm Cash
                              </button>
                              <button
                                style={S.btnReject}
                                onClick={() => openModal(r, "reject")}
                              >
                                ✕ Not Paid
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── Section 2: Confirmed — Pending Bank Deposit ── */}
            <div style={S.section}>
              <div style={S.sectionHead}>
                <div style={{ ...S.dot, background: "#3b82f6" }} />
                <span style={S.sectionTitle}>Confirmed — Pending Bank Deposit</span>
                <span style={{ ...S.pill, background: "#dbeafe", color: "#1d4ed8" }}>
                  {confirmedRows.length}
                </span>
                {confirmedRows.length > 0 && (
                  <button style={S.btnExcel} onClick={exportConfirmedToExcel}>
                    📥 Export Excel
                  </button>
                )}
              </div>

              {confirmedRows.length === 0 ? (
                <div style={{ ...S.card, ...S.empty }}>
                  <div style={S.emptyIcon}>🏦</div>
                  <p style={S.emptyTitle}>All deposited!</p>
                  <p style={S.emptySub}>No confirmed cash waiting to be sent to the bank.</p>
                </div>
              ) : (
                <div style={S.card}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        {["Driver", "Plan", "Amount", "Confirmed At", "Collected By", "Action"].map((h) => (
                          <th key={h} style={S.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {confirmedRows.map((r) => (
                        <tr key={r._id}>
                          <td style={S.td}>
                            <p style={S.driverName}>{r.driverId?.name || "—"}</p>
                            <p style={S.driverPhone}>{r.driverId?.phone || ""}</p>
                          </td>
                          <td style={S.td}>
                            <span style={S.badge}>{r.planId?.name || "—"}</span>
                          </td>
                          <td style={S.td}>
                            <span style={S.amount}>₹{r.amount?.toLocaleString()}</span>
                          </td>
                          <td style={{ ...S.td, fontSize: 12.5, color: "#6b7280" }}>
                            {fmt(r.paidAt)}
                          </td>
                          <td style={S.td}>
                            {r.collectedBy?.name
                              ? <span style={{ fontSize: 13 }}>{r.collectedBy.name}</span>
                              : <span style={S.muted}>—</span>}
                          </td>
                          <td style={S.td}>
                            <button
                              style={S.btnDeposit}
                              onClick={() => openModal(r, "deposit")}
                            >
                              🏦 Mark Deposited
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          ...S.toast,
          background: toast.ok ? "#1e293b" : "#7f1d1d",
          borderLeft: `4px solid ${toast.ok ? "#22c55e" : "#ef4444"}`,
        }}>
          {toast.ok ? "✅ " : "❌ "}{toast.msg}
        </div>
      )}

      {/* ── CONFIRM MODAL ── */}
      {selected && modalType === "confirm" && (
        <div style={S.overlay} onClick={closeModal}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...S.modalHeader, borderBottom: "1px solid #f3f4f6", background: "#f0fdf4" }}>
              <h3 style={{ ...S.modalTitle, color: "#15803d" }}>✓ Confirm Cash Payment</h3>
              <button style={S.modalClose} onClick={closeModal}>×</button>
            </div>
            <div style={S.modalBody}>
              <div style={{ ...S.banner, marginTop: 14 }}>
                <span>ℹ️</span>
                <p style={{ margin: 0 }}>
                  Confirming will mark this payment as received and immediately activate the driver's subscription.
                </p>
              </div>
              <div style={S.detailCard}>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Driver</span>
                  <span style={S.detailValue}>{selected.driverId?.name}</span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Phone</span>
                  <span style={S.detailValue}>{selected.driverId?.phone}</span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Plan</span>
                  <span style={S.badge}>{selected.planId?.name}</span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Amount</span>
                  <span style={S.detailAmount}>₹{selected.amount?.toLocaleString()}</span>
                </div>
              </div>
              <label style={S.fieldLabel}>Notes (optional)</label>
              <textarea
                style={S.fieldInput}
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Cash received at front desk"
              />
              <div style={S.modalActions}>
                <button style={S.btnCancel} onClick={closeModal}>Cancel</button>
                <button style={S.btnActivate} onClick={handleConfirm} disabled={busy}>
                  {busy ? "Confirming…" : "✓ Confirm & Activate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── REJECT MODAL ── */}
      {selected && modalType === "reject" && (
        <div style={S.overlay} onClick={closeModal}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...S.modalHeader, borderBottom: "1px solid #f3f4f6", background: "#fff7f7" }}>
              <h3 style={{ ...S.modalTitle, color: "#b91c1c" }}>✕ Mark as Not Paid</h3>
              <button style={S.modalClose} onClick={closeModal}>×</button>
            </div>
            <div style={S.modalBody}>
              <div style={{ ...S.banner, ...S.bannerDanger, marginTop: 14 }}>
                <span>⚠️</span>
                <p style={{ margin: 0 }}>
                  This will reject the payment. The driver's subscription will <strong>NOT</strong> be activated.
                </p>
              </div>
              <div style={S.detailCard}>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Driver</span>
                  <span style={S.detailValue}>{selected.driverId?.name}</span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Phone</span>
                  <span style={S.detailValue}>{selected.driverId?.phone}</span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Amount</span>
                  <span style={{ ...S.detailAmount, color: "#b91c1c" }}>₹{selected.amount?.toLocaleString()}</span>
                </div>
              </div>
              <label style={S.fieldLabel}>
                Reason for rejection <span style={S.fieldReq}>*</span>
              </label>
              <textarea
                style={{ ...S.fieldInput, ...S.fieldInputDanger }}
                rows={2}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Driver did not pay cash at office"
              />
              <div style={S.modalActions}>
                <button style={S.btnCancel} onClick={closeModal}>Cancel</button>
                <button style={S.btnRejectMd} onClick={handleReject} disabled={busy}>
                  {busy ? "Rejecting…" : "✕ Mark Not Paid"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MARK DEPOSITED MODAL ── */}
      {selected && modalType === "deposit" && (
        <div style={S.overlay} onClick={closeModal}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...S.modalHeader, borderBottom: "1px solid #f3f4f6", background: "#eff6ff" }}>
              <h3 style={{ ...S.modalTitle, color: "#1d4ed8" }}>🏦 Mark as Deposited to Bank</h3>
              <button style={S.modalClose} onClick={closeModal}>×</button>
            </div>
            <div style={S.modalBody}>
              <div style={{ ...S.banner, marginTop: 14 }}>
                <span>💡</span>
                <p style={{ margin: 0 }}>
                  Confirm this cash has been physically deposited. Superadmin will verify after.
                </p>
              </div>
              <div style={S.detailCard}>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Driver</span>
                  <span style={S.detailValue}>{selected.driverId?.name}</span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Phone</span>
                  <span style={S.detailValue}>{selected.driverId?.phone}</span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Plan</span>
                  <span style={S.badge}>{selected.planId?.name}</span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Amount</span>
                  <span style={S.detailAmount}>₹{selected.amount?.toLocaleString()}</span>
                </div>
                {selected.paidAt && (
                  <div style={S.detailRow}>
                    <span style={S.detailLabel}>Confirmed At</span>
                    <span style={{ ...S.detailValue, fontSize: 12.5 }}>{fmt(selected.paidAt)}</span>
                  </div>
                )}
              </div>
              <label style={S.fieldLabel}>Receipt / Reference Number (optional)</label>
              <input
                style={{ ...S.fieldInput, resize: "none" }}
                type="text"
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
                placeholder="e.g. HDFC2024061101"
              />
              <div style={S.modalActions}>
                <button style={S.btnCancel} onClick={closeModal}>Cancel</button>
                <button style={S.btnBank} onClick={handleMarkDeposited} disabled={busy}>
                  {busy ? "Saving…" : "🏦 Confirm Deposit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}